// =====================================================
// FORMATAÇÃO E VALIDAÇÃO DE DADOS
// =====================================================

// ===== CPF =====
export function formatCPF(value: string): string {
  const numbers = value.replace(/\D/g, "");
  if (numbers.length <= 11) {
    return numbers
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }
  return value;
}

export function validateCPF(cpf: string): boolean {
  const numbers = cpf.replace(/\D/g, "");
  if (numbers.length !== 11) return false;

  // Verificar sequências iguais
  if (/^(\d)\1{10}$/.test(numbers)) return false;

  // Validar dígitos verificadores
  let sum = 0;
  let remainder;

  for (let i = 1; i <= 9; i++) {
    sum += parseInt(numbers.substring(i - 1, i)) * (11 - i);
  }

  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(numbers.substring(9, 10))) return false;

  sum = 0;
  for (let i = 1; i <= 10; i++) {
    sum += parseInt(numbers.substring(i - 1, i)) * (12 - i);
  }

  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(numbers.substring(10, 11))) return false;

  return true;
}

// ===== CNPJ =====
export function formatCNPJ(value: string): string {
  const numbers = value.replace(/\D/g, "");
  if (numbers.length <= 14) {
    return numbers
      .replace(/(\d{2})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1/$2")
      .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
  }
  return value;
}

export function validateCNPJ(cnpj: string): boolean {
  const numbers = cnpj.replace(/\D/g, "");
  if (numbers.length !== 14) return false;

  // Verificar sequências iguais
  if (/^(\d)\1{13}$/.test(numbers)) return false;

  // Validar primeiro dígito
  let length = numbers.length - 2;
  let digits = numbers.substring(0, length);
  const dig1 = numbers.substring(length);
  let sum = 0;
  let pos = length - 7;

  for (let i = length; i >= 1; i--) {
    sum += parseInt(digits.charAt(length - i)) * pos--;
    if (pos < 2) pos = 9;
  }

  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(dig1.charAt(0))) return false;

  // Validar segundo dígito
  length = length + 1;
  digits = numbers.substring(0, length);
  sum = 0;
  pos = length - 7;

  for (let i = length; i >= 1; i--) {
    sum += parseInt(digits.charAt(length - i)) * pos--;
    if (pos < 2) pos = 9;
  }

  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(dig1.charAt(1))) return false;

  return true;
}

// ===== DOCUMENTO (CPF ou CNPJ) =====
export function formatDocument(value: string): string {
  const numbers = value.replace(/\D/g, "");
  if (numbers.length <= 11) {
    return formatCPF(value);
  }
  return formatCNPJ(value);
}

export function validateDocument(value: string): boolean {
  const numbers = value.replace(/\D/g, "");
  if (numbers.length === 11) {
    return validateCPF(value);
  }
  if (numbers.length === 14) {
    return validateCNPJ(value);
  }
  return false;
}

// ===== TELEFONE =====
export function formatPhone(value: string): string {
  const numbers = value.replace(/\D/g, "");

  // Celular com DDD: (11) 99999-9999
  if (numbers.length === 11) {
    return numbers.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  }

  // Fixo com DDD: (11) 9999-9999
  if (numbers.length === 10) {
    return numbers.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
  }

  // Celular sem DDD: 99999-9999
  if (numbers.length === 9) {
    return numbers.replace(/(\d{5})(\d{4})/, "$1-$2");
  }

  // Fixo sem DDD: 9999-9999
  if (numbers.length === 8) {
    return numbers.replace(/(\d{4})(\d{4})/, "$1-$2");
  }

  // Parcial
  if (numbers.length > 2) {
    return numbers
      .replace(/(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{4,5})(\d)/, "$1-$2");
  }

  return numbers;
}

export function isCellPhone(phone: string): boolean {
  const numbers = phone.replace(/\D/g, "");
  // Celular tem 11 dígitos (com DDD) ou 9 (sem DDD) começando com 9
  return (
    (numbers.length === 11 && numbers[2] === "9") ||
    (numbers.length === 9 && numbers[0] === "9")
  );
}

// ===== CEP =====
export function formatCEP(value: string): string {
  const numbers = value.replace(/\D/g, "");
  return numbers.replace(/(\d{5})(\d{1,3})/, "$1-$2");
}

// ===== EMAIL =====
export function validateEmail(email: string): boolean {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email.toLowerCase());
}

// ===== NOME =====
export function capitalizeName(name: string): string {
  const exceptions = ["da", "de", "do", "das", "dos", "e"];

  return name
    .toLowerCase()
    .split(" ")
    .map((word, index) => {
      // Primeira e última palavra sempre maiúscula
      if (index === 0 || index === name.split(" ").length - 1) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      // Exceções ficam minúsculas
      if (exceptions.includes(word)) {
        return word;
      }
      // Demais palavras com primeira letra maiúscula
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
}

// ===== MOEDA =====
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

export function parseCurrency(value: string | number): number {
  if (typeof value === "number") return value;
  if (!value) return 0;
  // Remove tudo exceto números
  const numbers = value.replace(/\D/g, "");
  if (!numbers) return 0;
  return Number(numbers) / 100;
}

export function formatCurrencyInput(value: string): string {
  const numbers = value.replace(/\D/g, "");
  const amount = Number(numbers) / 100;
  return formatCurrency(amount);
}

// ===== DATA =====
export function formatDate(value: string): string {
  const numbers = value.replace(/\D/g, "");
  return numbers
    .replace(/(\d{2})(\d)/, "$1/$2")
    .replace(/(\d{2})(\d)/, "$1/$2")
    .replace(/(\d{4})(\d)/, "$1");
}

export function validateDate(date: string): boolean {
  const [day, month, year] = date.split("/").map(Number);
  if (!day || !month || !year) return false;

  const dateObj = new Date(year, month - 1, day);
  return (
    dateObj.getFullYear() === year &&
    dateObj.getMonth() === month - 1 &&
    dateObj.getDate() === day
  );
}

// ===== NÚMERO GENÉRICO =====
export function formatNumber(value: string | number): string {
  return new Intl.NumberFormat("pt-BR").format(Number(value));
}

// ===== APLICAR FORMATAÇÃO AUTOMÁTICA =====
export function applyMask(
  value: string,
  type:
    | "cpf"
    | "cnpj"
    | "document"
    | "phone"
    | "cep"
    | "currency"
    | "date"
    | "name",
): string {
  if (!value) return "";

  switch (type) {
    case "cpf":
      return formatCPF(value);
    case "cnpj":
      return formatCNPJ(value);
    case "document":
      return formatDocument(value);
    case "phone":
      return formatPhone(value);
    case "cep":
      return formatCEP(value);
    case "currency":
      return formatCurrencyInput(value);
    case "date":
      return formatDate(value);
    case "name":
      return capitalizeName(value);
    default:
      return value;
  }
}
